"use client";

import {get_mega_menus_proxy} from '@core/services/megamenu';
import {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useAuthContext} from "@core/helpers/client/contexts/AuthContext";

export const MenusContext = createContext(null);

// `initialMenus` (optional, all sites; from jument's MenusProvider and memory-market's ServerMenusProvider): menus already
// fetched on the server (Layout `menu_keys`, helpers/server/menus.js). They render in the server HTML and are not fetched
// again on mount. `initialMenuKeys`: the keys the server asked for (default: the keys of `initialMenus`); the other
// `menuKeys` are fetched after hydration as before, and `menus_loading` is true only while those load. A later refetch
// (token change) runs without the loading state and merges the result. Without `initialMenus` the behaviour is unchanged.
export const MenusContextProvider = ({children, menuKeys = [], initialMenus, initialMenuKeys}) => {
	const megaMenuKeys = useMemo(() => menuKeys, [menuKeys]);
	const seeded = initialMenus !== undefined && initialMenus !== null;
	// with server menus: the keys still to fetch on mount
	const [mount_keys] = useState(() => {
		if (!seeded) return null;
		const asked = Array.isArray(initialMenuKeys) ? initialMenuKeys : Object.keys(initialMenus);
		return menuKeys.filter((key) => !asked.includes(key));
	});
	const [menus, set_menus] = useState(() => (seeded ? initialMenus : {}));
	const [menus_loading, set_menus_loading] = useState(() => !seeded || mount_keys.length > 0)
	const first_run = useRef(true);
	const {token} = useAuthContext()

	const get_menus = async (keys = megaMenuKeys, silent = false) => {
		if (keys?.length > 0) {
			if (!silent) set_menus_loading(true);
			const result = await get_mega_menus_proxy({menu_keys: keys})

			const _menus = {}
			if (result?.status) {
				result?.data?.forEach(item => {
					_menus[item?.key] = item?.value
				})
				set_menus(seeded ? (prev) => ({...prev, ..._menus}) : _menus)
			}
			if (!silent) set_menus_loading(false);
		}
	}

	useEffect(() => {
		const is_first = first_run.current;
		first_run.current = false;
		// the server menus are already in state: only what the server did not render
		if (seeded && is_first) {
			get_menus(mount_keys)
			return;
		}
		get_menus(megaMenuKeys, seeded)
	}, [token, megaMenuKeys])

	const values = useMemo(() => ({
		menus,
		set_menus,
		menus_loading,
		set_menus_loading
	}), [menus, menus_loading]);

	return (
		<MenusContext.Provider value={{...values}}>
			{children}
		</MenusContext.Provider>
	);
};

export const useMenusContext = () => useContext(MenusContext)
